import { Flex } from '@mantine/core';
import { sumBy } from 'lodash';
import numeral from 'numeral';
import { useNavigate } from 'react-router-dom';

import { Batch, BatchTypeEnum, DeclarationStatus, EnvironmentCountries, FilterRisksValues, TemplateType } from '@e-origin/shared';

import { GoToDeclarationIcon, GoToDeclarationIconTabLocation, StatisticLink, TableColumnProps } from '@components';
import { BATCH_LIST_PERCENTAGE } from '@constants';

export const useBuildSendingColumns = ({ settings, batchView }): TableColumnProps<Batch>[] => {
  const navigate = useNavigate();

  const handleStatisticsClick = (row: Batch, tab: string, filter?: FilterRisksValues) => {
    if (row.status.value !== 'failed') {
      let path = `/declarations?batchId=${row._id}&tab=${tab}&batchName=${row.name}`;
      if (filter) {
        path = `${path}&riskValues=${filter}&batchName=${row.name}`;
      }
      navigate(path);
    }
  };

  const countSentPostArrival = (row: Batch): number => {
    if (!row.statistics?.in?.PRE?.length) {
      return row.statistics?.sent || 0;
    }
    if (row.statistics?.in?.POST.length) {
      return sumBy(row.statistics.in.POST, (item) => item.total) || 0;
    }

    if (settings.country === EnvironmentCountries.BE && row.templateType === TemplateType.LOW_VALUE_H7) {
      return row.statistics?.sent || 0;
    }

    return 0;
  };

  return [
    ...(((settings?.country === EnvironmentCountries.BE && [BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)) ||
      settings?.country === EnvironmentCountries.NL) &&
    !settings?.isForwarder
      ? [
          {
            name: 'Pre-Arrival Accepted',
            selector: 'statistics.preAccepted',
            sortable: true,
            width: '100px',
            cell: (row) => (
              <Flex gap={5}>
                <StatisticLink
                  isCompleted={row.statistics?.preAccepted > 0 && row.statistics?.preAccepted === row.statistics?.total}
                  onClick={() => {
                    if ([BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)) {
                      if (row.status.value !== 'failed') {
                        navigate(`/declaration-groups?batchId=${row._id}`);
                      }
                    } else {
                      handleStatisticsClick(row, DeclarationStatus.ACCEPTED);
                    }
                  }}
                >
                  <span data-pw={`cell-pre-arrival-accepted-${row.name}`} style={row.statistics?.inControl > 0 ? { color: '#ff3d3d' } : {}}>
                    {row.statistics ? `${row.statistics?.preAccepted || 0}/${row.statistics?.total}` : 0}
                  </span>
                </StatisticLink>
              </Flex>
            ),
          },
        ]
      : []),
    {
      name: 'Sent post-Arrival',
      selector: 'statistics.sent',
      sortable: true,
      width: '100px',
      cell: (row) => (
        <Flex gap={5}>
          <StatisticLink
            disabled={row.status.value === 'failed'}
            isCompleted={row.statistics && row.statistics?.released > 0 && row.statistics?.released === row.statistics?.total}
            onClick={() => {
              if ([BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)) {
                if (row.status.value !== 'failed') {
                  navigate(`/declaration-groups?batchId=${row._id}`);
                }
              } else {
                handleStatisticsClick(row, DeclarationStatus.SENT);
              }
            }}
          >
            <span data-pw={`cell-sent-post-arrival-${row.name}`} style={row.statistics?.inControl > 0 ? { color: '#ff3d3d' } : {}}>
              {countSentPostArrival(row)}/{row.statistics?.total || 0}
            </span>
          </StatisticLink>
          {[BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView) && (
            <GoToDeclarationIcon batchId={row?._id} tab={GoToDeclarationIconTabLocation.DOCUMENTS} source="batches" />
          )}
        </Flex>
      ),
    },
    {
      name: 'In Progress %',
      selector: 'statistics.inProgress',
      sortable: true,
      width: '100px',
      cell: (row, rowIdx) => (
        <Flex gap={5}>
          <StatisticLink
            disabled={row.status.value === 'failed'}
            onClick={() => {
              handleStatisticsClick(row, DeclarationStatus.ACCEPTED);
            }}
          >
            {row.statistics ? numeral((row.statistics.accepted / row.statistics.total) * BATCH_LIST_PERCENTAGE).format('0.[00]') : 0}
          </StatisticLink>
          {row.statistics.accepted > 0 && <GoToDeclarationIcon batchId={row?._id} status={DeclarationStatus.ACCEPTED} source="batches" />}
        </Flex>
      ),
    },
    {
      name: 'Rejected %',
      selector: 'statistics.rejected',
      sortable: true,
      cell: (row) => (
        <Flex gap={5}>
          <StatisticLink
            data-pw={`cell-rejected-${row.name}`}
            disabled={row.status.value === 'failed'}
            onClick={() => {
              handleStatisticsClick(row, DeclarationStatus.REJECTED);
            }}
          >
            {row.statistics ? numeral((row.statistics.rejected / row.statistics.total) * BATCH_LIST_PERCENTAGE).format('0.[00]') : 0}
          </StatisticLink>
          {row.statistics.rejected > 0 && <GoToDeclarationIcon batchId={row?._id} status={DeclarationStatus.REJECTED} source="batches" />}
        </Flex>
      ),
    },
    {
      name: 'In control %',
      selector: 'statistics.inControl',
      sortable: true,
      cell: (row) => (
        <Flex gap={5}>
          <StatisticLink
            disabled={row.status.value === 'failed'}
            onClick={() => {
              handleStatisticsClick(row, DeclarationStatus.IN_CONTROL);
            }}
          >
            {row.statistics ? numeral((row.statistics.inControl / row.statistics.total) * BATCH_LIST_PERCENTAGE).format('0.[00]') : 0}
          </StatisticLink>
          {row.statistics.inControl > 0 && <GoToDeclarationIcon batchId={row?._id} status={DeclarationStatus.IN_CONTROL} source="batches" />}
        </Flex>
      ),
    },
    {
      name: 'Not released %',
      selector: 'statistics.notReleased',
      sortable: true,
      cell: (row) => (
        <Flex gap={5}>
          <StatisticLink
            disabled={row.status.value === 'failed'}
            onClick={() => {
              handleStatisticsClick(row, DeclarationStatus.NOT_RELEASED);
            }}
          >
            {row.statistics ? numeral((row.statistics.notReleased / row.statistics.total) * BATCH_LIST_PERCENTAGE).format('0.[00]') : 0}
          </StatisticLink>
          {row.statistics.notReleased > 0 && <GoToDeclarationIcon batchId={row?._id} status={DeclarationStatus.NOT_RELEASED} source="batches" />}
        </Flex>
      ),
    },
    {
      name: 'Released %',
      selector: 'statistics.released',
      sortable: true,
      cell: (row) => (
        <Flex gap={5}>
          <StatisticLink
            data-pw={`cell-released-${row.name}`}
            disabled={row.status.value === 'failed'}
            onClick={() => {
              handleStatisticsClick(row, DeclarationStatus.RELEASED);
            }}
          >
            {row.statistics ? numeral((row.statistics.released / row.statistics.total) * BATCH_LIST_PERCENTAGE).format('0.[00]') : 0}
          </StatisticLink>
          {row.statistics.released > 0 && <GoToDeclarationIcon batchId={row?._id} status={DeclarationStatus.RELEASED} source="batches" />}
        </Flex>
      ),
    },
    ...(batchView === BatchTypeEnum.HIGH_VALUES_EXPORT
      ? [
          {
            name: 'Exported %',
            selector: 'statistics.exported',
            sortable: true,
            cell: (row) => (
              <Flex gap={5}>
                <StatisticLink
                  disabled={row.status.value === 'failed'}
                  onClick={() => {
                    handleStatisticsClick(row, DeclarationStatus.EXPORTED);
                  }}
                >
                  {row.statistics ? numeral((row.statistics.exported / row.statistics.total) * BATCH_LIST_PERCENTAGE).format('0.[00]') : 0}
                </StatisticLink>
                {row.statistics.exported > 0 && <GoToDeclarationIcon batchId={row?._id} status={DeclarationStatus.EXPORTED} source="batches" />}
              </Flex>
            ),
          },
        ]
      : []),
    {
      name: 'Invalidated %',
      selector: 'statistics.invalidated',
      sortable: true,
      cell: (row) => (
        <Flex gap={5}>
          <StatisticLink
            disabled={row.status.value === 'failed'}
            onClick={() => {
              handleStatisticsClick(row, DeclarationStatus.INVALIDATED);
            }}
          >
            {row.statistics ? numeral((row.statistics.invalidated / row.statistics.total) * BATCH_LIST_PERCENTAGE).format('0.[00]') : 0}
          </StatisticLink>
          {row.statistics.invalidated > 0 && <GoToDeclarationIcon batchId={row?._id} status={DeclarationStatus.INVALIDATED} source="batches" />}
        </Flex>
      ),
    },
  ];
};
